"use client";

import * as React from "react";
import { SideOverlayDrawer } from "./side-overlay-drawer";
import { Input } from "./input";
import { Button } from "./button";
import { Typography } from "./typography";
import { PrivacyShieldIcon } from "../icons";
import { isValidWhatsAppNumber } from "@artha/utils";

const WHATSAPP_PLACEHOLDER = "+11234567890";
const WHATSAPP_HELPER_TEXT = "Include country code. Example: +11234567890";

export interface WhatsAppUpdatesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  onPhoneNumberChange?: (value: string) => void;
  isEditing?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  title?: string;
  description?: string;
  firstPoint?: string;
  secondPoint?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  phoneHelperText?: string;
  privacyTitle?: string;
  privacyDescription?: string;
  removeLabel?: string;
  editLabel?: string;
  saveLabel?: string;
  savingLabel?: string;
  footerConsentText?: string;
}

export function WhatsAppUpdatesDrawer({
  isOpen,
  onClose,
  phoneNumber,
  onPhoneNumberChange,
  isEditing = true,
  onEdit,
  onRemove,
  onSave,
  isSaving,
  title = "Subscribe to Whatsapp updates",
  description = "Get WhatsApp updates from artha.link that help you stay consistent and earn from your audience.",
  firstPoint = "Ready-to-paste content you can share in seconds.",
  secondPoint = "Stay active without overthinking what to post.",
  phoneLabel = "WhatsApp number",
  phonePlaceholder = WHATSAPP_PLACEHOLDER,
  phoneHelperText = WHATSAPP_HELPER_TEXT,
  privacyTitle = "Privacy",
  privacyDescription = "artha.link respects your privacy and will not use your WhatsApp number for any marketing or promotional campaigns.",
  removeLabel = "Remove subscription",
  editLabel = "Edit",
  saveLabel = "Save subscription",
  savingLabel = "Saving...",
  footerConsentText = "By continuing, you agree to receive WhatsApp messages from artha.link. You can opt out anytime.",
}: WhatsAppUpdatesDrawerProps) {
  const hasSavedNumber = phoneNumber.trim().length > 0;
  const isPhoneValid = isValidWhatsAppNumber(phoneNumber);

  return (
    <SideOverlayDrawer isOpen={isOpen} onClose={onClose} title={title} onBack={onClose}>
      <div className="flex min-h-full flex-col text-[#344054]">
        <div className="flex-1 space-y-6">
          <div className="rounded-[20px] border border-black/5 bg-white px-4 py-4">
            <Typography variant="p" className="mt-2 text-sm leading-6 text-[#475467]">
              {description}
            </Typography>
            <ul className="mt-4 space-y-2 text-sm leading-6 text-[#475467]">
              <li className="flex items-start gap-2">
                <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#ED5AA9]" />
                <Typography variant="p" className="text-sm leading-6 text-[#475467]">
                  {firstPoint}
                </Typography>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#ED5AA9]" />
                <Typography variant="p" className="text-sm leading-6 text-[#475467]">
                  {secondPoint}
                </Typography>
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <label htmlFor="whatsapp-number" className="text-sm font-medium text-[#667085]">
              {phoneLabel}
            </label>
            <Input
              id="whatsapp-number"
              value={phoneNumber}
              onChange={(event) => onPhoneNumberChange?.(event.target.value)}
              placeholder={phonePlaceholder}
              inputMode="tel"
              className="h-[46px]"
              disabled={!isEditing && hasSavedNumber}
            />
            <Typography variant="p" className="text-xs leading-5 text-[#667085]">
              {phoneHelperText}
            </Typography>
          </div>

          <div className="flex items-start gap-3 rounded-[18px] bg-[#FFF7E7] px-4 py-4">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#12B76A]">
              <PrivacyShieldIcon />
            </span>
            <div>
              <Typography variant="small" className="text-sm font-semibold text-[#344054]">
                {privacyTitle}
              </Typography>
              <Typography variant="p" className="mt-1 text-xs leading-5 text-[#475467]">
                {privacyDescription}
              </Typography>
            </div>
          </div>

          {hasSavedNumber && !isEditing ? (
            <div className="flex items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={onRemove}
                className="text-sm font-medium text-[#B42318] underline-offset-4 hover:underline"
              >
                {removeLabel}
              </button>
              <Button type="button" variant="outline" onClick={onEdit} className="min-w-24">
                {editLabel}
              </Button>
            </div>
          ) : (
            <div className="flex justify-end pt-2">
              <Button onClick={onSave} disabled={!isPhoneValid || isSaving} className="min-w-36">
                {isSaving ? savingLabel : saveLabel}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-black/5 pt-4">
          <Typography variant="p" className="text-xs leading-5 text-[#98A2B3]">
            {footerConsentText}
          </Typography>
        </div>
      </div>
    </SideOverlayDrawer>
  );
}
